/**
 * Snapshot, restore, and content-hash for the sim state (AD-007a). The three
 * uses this exists for: the regression suite (record a trace once, assert the
 * hash forever), lockstep multiplayer desync checks, and saving a flight
 * mid-air.
 *
 * The snapshot omits the derived `AircraftSpec`, because it is a pure function
 * of the aircraft key and the tunables, both of which *are* stored — restoring
 * rebuilds it. Everything else round-trips field for field, and the hash is
 * taken over the canonical form so key order can never change a digest.
 */

import { AIRCRAFT_KEYS } from "./aircraft-table.ts";
import { canonicalJson, contentHash } from "./hash.ts";
import { PILOT_CONDITIONS, type PilotState } from "./physiology.ts";
import {
  readBoolean,
  readMember,
  readMemberArray,
  readNumber,
  readObject,
  requireObject,
  type PlainObject,
} from "./plain.ts";
import { RNG_STREAM_NAMES, type RngBankState, type RngStreamState } from "./rng.ts";
import { createSimConfig, SIM_EVENT_KINDS, type SimState } from "./state.ts";
import { FLIGHT_MODEL_TIERS, type RealismSettings } from "./tiers.ts";
import { DEFAULT_TUNABLES, TUNABLE_KEYS, type Tunables } from "./tunables.ts";
import { WEATHER_KINDS } from "./wind.ts";
import type { BodyRates, Quat, Vec3 } from "./math3d.ts";

/**
 * Bumped when the snapshot layout changes in a way older files cannot satisfy.
 * Version 2 added the `gunnery` and `ai` random streams (AD-007g), so a
 * version-1 file's `rng` block is missing two required members. Version 3 added
 * the undercarriage: a `gear` block and the readout's `groundHeightM` /
 * `altitudeAglM` pair, none of which a version-2 file carries. Version 4 added
 * the `flak` random stream (AD-010) — both 3-era additions merged in the same
 * wave, but from parallel lanes that each claimed 3, so flak takes 4.
 *
 * Version 5 is wind (AD-007k), and it is a version bump rather than a silent
 * addition because a version-4 file cannot satisfy the reader in three separate
 * places: `config.weather` did not exist, `config.realism` has no
 * `realisticWeather` member, and `readout` has no `windMps`. No new *mutable*
 * state and no new random stream came with it — the original's wind has no
 * gusts and touches no RNG, so wind is recomputed from altitude and config every
 * tick and the `rng` block is unchanged from version 4.
 *
 * **Version 6 is the `airship` random stream** — `MG.DAT` gave the Zeppelin four
 * gun stations and they draw their graze scatter and their hits off a stream of
 * their own (`rng.ts`, `combat/airship-pass.ts`). Same shape as the version-2
 * and version-4 bumps: the `rng` block is written and read by stream name, so a
 * version-5 file is simply missing a required member. Nothing else in the
 * layout moved — an airship has no `SimState`, so no aeroplane gained a field.
 *
 * **Version 7 is `wingIncidenceRad`** (`aero-tunables.ts`), and it is the
 * cheapest kind of bump to overlook: the tunable ships at **0**, so this build
 * flies a version-6 tape's stick to a bit-identical trajectory — 33 airframes,
 * 1,200 ticks, all four axes deflected, every position and quaternion equal to
 * 17 significant figures. Nothing about the *flight* changed. What changed is
 * the file, because `readTunables` above reads **every** key in `TUNABLE_KEYS`
 * and a version-6 snapshot has no member under that name. A reader that let the
 * default fill it in would be right today and silently wrong the day the value
 * moves off zero, which is precisely the day it matters, so the missing member
 * is treated as the breaking change it is and the file refuses.
 *
 * `engine-fingerprint.ts` moved for the same reason and needed no help doing
 * it, which is the behaviour its own header promises: the guard tracks the
 * tunable *set*, not just the values, so adding a key that does nothing yet
 * still invalidates the pinned tape rather than waiting to be remembered.
 *
 * **Version 8 is `spinEntryMinSideslipMps`** (`stall-tunables.ts`), and unlike
 * version 7 it arrives with the flight changing underneath it: AD-007o switched
 * `wingIncidenceRad` on at its measured 5.5°, so this build genuinely does fly a
 * version-7 tape's stick somewhere else. The new key is the same shape of bump
 * as 7 — `readTunables` requires every member of `TUNABLE_KEYS` and a version-7
 * snapshot has none under that name — but the tape had to be re-cut regardless,
 * and this time because the aeroplane moved rather than because the file did.
 *
 * **Version 9 is `pilot.bailedOut`** (AD-021, `combat/parachute.ts`) — the first
 * bump in a while that adds a genuine mutable field rather than a tunable key.
 * A version-8 file has no member under that name and `readPilot` requires one,
 * for the reason versions 7 and 8 give: a reader that defaulted it would be
 * right for every tape recorded so far and silently wrong for the first one
 * recorded after somebody jumps, which is the only tape where it matters.
 *
 * **The flight itself is unmoved.** `bailedOut` ships false, no aeroplane can
 * set it without an explicit `bailOut` command, and `engine-fingerprint.ts`'s
 * airframe probe never issues one — so this build flies a version-8 tape's
 * stick to the identical trajectory. The fingerprint moves anyway, because
 * `SNAPSHOT_VERSION` is literally one of its four inputs, and the pinned tape is
 * re-cut with it. That is the guard behaving exactly as its header promises.
 *
 * **Version 10 is `config.ammunition`** (G35), and unlike 9 the flight really
 * does move — twice, and both were bugs.
 *
 * The field itself is the mission's AMMUNITION choice, which until now reached
 * only the balloon-damage model and never the shooter's own aeroplane. It is in
 * the config beside `weather` for `weather`'s reasons, and in the snapshot
 * because it now decides a jam threshold: two flights differing only in their
 * belt would otherwise hash identically while diverging, which is the one thing
 * this file exists to prevent.
 *
 * **What moved the aeroplane**, in the order the tape notices it:
 *
 * 1. **The tunable key was renamed** `gunDamagedJitterBam` → `gunIncendiaryJitterBam`,
 *    because the old name was a claim and the claim was false
 *    (`combat/gunnery.ts` carries the disassembly). `tunables` is serialized
 *    whole, so **a rename alone moves every state hash** with no behaviour
 *    change at all — worth knowing before anyone renames another one.
 * 2. **The jitter's condition changed** from "the airframe is damaged" to "the
 *    belt is incendiary". That is a real change to how rounds leave the guns and
 *    to how often the `gunnery` stream is drawn from, so this build genuinely
 *    flies a version-9 tape's stick somewhere else.
 *
 * **Everything else in that lane is provably flight-neutral**, and it was proved
 * rather than asserted, by the strip-and-replay method `lessons.md` records —
 * done *before* re-cutting, because re-cutting destroys the evidence. Replaying
 * the version-9 tape on this build with the new field stripped, the tunable key
 * restored and the jitter's old predicate back in place reproduced **all 30
 * checkpoints and the guard**. So the `SimOptions`/`SimConfig` plumbing, the
 * ammunition type's move into `flight/guns.ts`, `jamThresholdFor`,
 * `expectedRoundsToJam` and `ammunitionJamScale` at its identity value for a
 * ball belt together move nothing.
 */
export const SNAPSHOT_VERSION = 10;

/** The rng block, written and read by stream name so the set can grow in one place. */
function snapshotRng(rng: RngBankState): Record<string, RngStreamState> {
  const entries = RNG_STREAM_NAMES.map((name) => [name, { ...rng[name] }] as const);
  return Object.fromEntries(entries);
}

export function toSnapshot(sim: SimState): Record<string, unknown> {
  const { config } = sim;
  return {
    version: SNAPSHOT_VERSION,
    config: {
      aircraft: config.aircraft,
      tier: config.tier,
      seed: config.seed,
      altitudeLapse: config.altitudeLapse,
      weather: config.weather,
      ammunition: config.ammunition,

      realism: { ...config.realism },
      tunables: { ...config.tunables },
    },
    tick: sim.tick,
    timeS: sim.timeS,
    position: { ...sim.position },
    velocity: { ...sim.velocity },
    orientation: { ...sim.orientation },
    rates: { ...sim.rates },
    engine: { ...sim.engine },
    guns: { ...sim.guns },
    pilot: { ...sim.pilot },
    damage: { ...sim.damage },
    stallModes: { spin: { ...sim.stallModes.spin }, spiral: { ...sim.stallModes.spiral } },
    gear: { ...sim.gear },
    readout: { ...sim.readout },
    rng: snapshotRng(sim.rng),
    events: [...sim.events],
    crashed: sim.crashed,
  };
}

/** Canonical JSON for one state — the exact bytes the hash is taken over. */
export function serializeSim(sim: SimState): string {
  return canonicalJson(toSnapshot(sim));
}

/** 64-bit fingerprint of a sim state. Equal hashes mean equal states, tick for tick. */
export function simStateHash(sim: SimState): string {
  return contentHash(serializeSim(sim));
}

function readVec3(source: PlainObject, key: string): Vec3 {
  const value = readObject(source, key);
  return { x: readNumber(value, "x"), y: readNumber(value, "y"), z: readNumber(value, "z") };
}

function readQuat(source: PlainObject, key: string): Quat {
  const value = readObject(source, key);
  return {
    x: readNumber(value, "x"),
    y: readNumber(value, "y"),
    z: readNumber(value, "z"),
    w: readNumber(value, "w"),
  };
}

function readRates(source: PlainObject, key: string): BodyRates {
  const value = readObject(source, key);
  return {
    roll: readNumber(value, "roll"),
    pitch: readNumber(value, "pitch"),
    yaw: readNumber(value, "yaw"),
  };
}

function readTunables(source: PlainObject): Tunables {
  const merged = { ...DEFAULT_TUNABLES };
  for (const key of TUNABLE_KEYS) {
    merged[key] = readNumber(source, key);
  }
  return merged;
}

function readRealism(source: PlainObject): RealismSettings {
  return {
    gunJams: readBoolean(source, "gunJams"),
    blackouts: readBoolean(source, "blackouts"),
    carburetorFreezes: readBoolean(source, "carburetorFreezes"),
    limitedFuel: readBoolean(source, "limitedFuel"),
    limitedAmmunition: readBoolean(source, "limitedAmmunition"),
    aircraftMayBeDamaged: readBoolean(source, "aircraftMayBeDamaged"),
    midairCollisions: readBoolean(source, "midairCollisions"),
    realisticWeather: readBoolean(source, "realisticWeather"),
  };
}

function readPilot(source: PlainObject): PilotState {
  return {
    hypoxia: readNumber(source, "hypoxia"),
    condition: readMember(source, "condition", PILOT_CONDITIONS),
    wounded: readBoolean(source, "wounded"),
    bleedOutSeconds: readNumber(source, "bleedOutSeconds"),
    alive: readBoolean(source, "alive"),
    bailedOut: readBoolean(source, "bailedOut"),
  };
}

function readStream(source: PlainObject, key: string): RngStreamState {
  const value = readObject(source, key);
  return {
    s0: readNumber(value, "s0"),
    s1: readNumber(value, "s1"),
    s2: readNumber(value, "s2"),
    s3: readNumber(value, "s3"),
  };
}

function readRngBank(source: PlainObject): RngBankState {
  const entries = RNG_STREAM_NAMES.map((name) => [name, readStream(source, name)] as const);
  return Object.fromEntries(entries) as RngBankState;
}

function restoreConfig(source: PlainObject): SimState["config"] {
  return createSimConfig({
    aircraft: readMember(source, "aircraft", AIRCRAFT_KEYS),
    tier: readMember(source, "tier", FLIGHT_MODEL_TIERS),
    seed: readNumber(source, "seed"),
    altitudeLapse: readBoolean(source, "altitudeLapse"),
    weather: readMember(source, "weather", WEATHER_KINDS),
    realism: readRealism(readObject(source, "realism")),
    tunables: readTunables(readObject(source, "tunables")),
  });
}

function restoreEngine(source: PlainObject): SimState["engine"] {
  return {
    throttle: readNumber(source, "throttle"),
    deliveredPowerW: readNumber(source, "deliveredPowerW"),
    carbIced: readBoolean(source, "carbIced"),
    carbThawSeconds: readNumber(source, "carbThawSeconds"),
    fuelKg: readNumber(source, "fuelKg"),
    fuelExhausted: readBoolean(source, "fuelExhausted"),
  };
}

function restoreGuns(source: PlainObject): SimState["guns"] {
  return {
    rounds: readNumber(source, "rounds"),
    capacity: readNumber(source, "capacity"),
    burstRounds: readNumber(source, "burstRounds"),
    jammed: readBoolean(source, "jammed"),
    permanentJam: readBoolean(source, "permanentJam"),
    clearAttempts: readNumber(source, "clearAttempts"),
    fireAccumulator: readNumber(source, "fireAccumulator"),
    clearHeld: readBoolean(source, "clearHeld"),
  };
}

function restoreDamage(source: PlainObject): SimState["damage"] {
  return {
    engine: readNumber(source, "engine"),
    wingLeft: readNumber(source, "wingLeft"),
    wingRight: readNumber(source, "wingRight"),
    controls: readNumber(source, "controls"),
    fuel: readNumber(source, "fuel"),
    wingsFailed: readBoolean(source, "wingsFailed"),
    peakLoadFactor: readNumber(source, "peakLoadFactor"),
  };
}

function restoreStallModes(source: PlainObject): SimState["stallModes"] {
  const spin = readObject(source, "spin");
  const spiral = readObject(source, "spiral");
  return {
    spin: {
      active: readBoolean(spin, "active"),
      direction: readNumber(spin, "direction"),
      recoverySeconds: readNumber(spin, "recoverySeconds"),
      elapsedSeconds: readNumber(spin, "elapsedSeconds"),
      lockoutSeconds: readNumber(spin, "lockoutSeconds"),
    },
    spiral: { active: readBoolean(spiral, "active"), severity: readNumber(spiral, "severity") },
  };
}

function restoreGear(source: PlainObject): SimState["gear"] {
  return {
    onGround: readBoolean(source, "onGround"),
    collapsed: readBoolean(source, "collapsed"),
    stopped: readBoolean(source, "stopped"),
  };
}

function restoreReadout(source: PlainObject): SimState["readout"] {
  return {
    airspeedMps: readNumber(source, "airspeedMps"),
    alphaRad: readNumber(source, "alphaRad"),
    betaRad: readNumber(source, "betaRad"),
    loadFactor: readNumber(source, "loadFactor"),
    stalled: readBoolean(source, "stalled"),
    altitudeM: readNumber(source, "altitudeM"),
    altitudeFt: readNumber(source, "altitudeFt"),
    groundHeightM: readNumber(source, "groundHeightM"),
    altitudeAglM: readNumber(source, "altitudeAglM"),
    climbRateMps: readNumber(source, "climbRateMps"),
    bankRad: readNumber(source, "bankRad"),
    turnLimited: readBoolean(source, "turnLimited"),
    grounded: readBoolean(source, "grounded"),
    windMps: readVec3(source, "windMps"),
  };
}

/** Rebuilds a sim state from a parsed snapshot, validating every field on the way in. */
export function fromSnapshot(value: unknown): SimState {
  const root = requireObject(value, "snapshot");
  const version = readNumber(root, "version");
  if (version !== SNAPSHOT_VERSION) {
    throw new RangeError(`snapshot version ${version} is not supported (expected ${SNAPSHOT_VERSION})`);
  }
  return {
    config: restoreConfig(readObject(root, "config")),
    tick: readNumber(root, "tick"),
    timeS: readNumber(root, "timeS"),
    position: readVec3(root, "position"),
    velocity: readVec3(root, "velocity"),
    orientation: readQuat(root, "orientation"),
    rates: readRates(root, "rates"),
    engine: restoreEngine(readObject(root, "engine")),
    guns: restoreGuns(readObject(root, "guns")),
    pilot: readPilot(readObject(root, "pilot")),
    damage: restoreDamage(readObject(root, "damage")),
    stallModes: restoreStallModes(readObject(root, "stallModes")),
    gear: restoreGear(readObject(root, "gear")),
    readout: restoreReadout(readObject(root, "readout")),
    rng: readRngBank(readObject(root, "rng")),
    events: readMemberArray(root, "events", SIM_EVENT_KINDS),
    crashed: readBoolean(root, "crashed"),
  };
}

export function deserializeSim(text: string): SimState {
  return fromSnapshot(JSON.parse(text));
}
